/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';

import { AiAssumption, Forecast } from '@futrli-sdk/predict';
import { matchConsolidationsRoutePath, matchReportingInternalRenderReportUrl } from '@futrli/shared-services';
import {
  ConsolidatedForecastDetail,
  ConsolidatedForecastListItem,
  ConsolidatedForecastUpdateRequest
} from '@futrli-sdk/consolidations';
import { LOADER_AREAS } from '@futrli/ui-components';

import { INITIAL_ROW_HEADER_WIDTH, PeriodType } from '../../../core/constants';
import {
  Page,
  QueryParams,
  VIEW_TYPES,
  ASSETS_CATEGORIES,
  LIABILITIES_CATEGORIES,
  OTHER_PL_CATEGORIES,
  ConsolidatedForecastReport
} from '../../../core/types';
import { useDetailContext } from './detail-context/detail-context';
import { useConsolidatedForecastContext } from './consolidated-forecast-context';
import { PREDICTIONS_SUB_PAGES_IDS } from '../const';
import { AllowedAssumptionTypes } from '../reports/actions/actions';
import { BulkUpdatedPredictionsPayload } from '../../../ui/assistant-side-panel/bulk-update/types';
import { fetchForecastThisNumberExplained } from '../../../core/api/forecast';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { TotalRow } from '../reports/data-grid/types';
import { SyncCompleteHandler } from './sync-complete-handler';

export interface PredictionsInfoContextValues {
  updateForecast: (
    forecast: Partial<Forecast> | Partial<ConsolidatedForecastUpdateRequest>,
    shouldRefetchReport?: boolean,
    loadingArea?: LOADER_AREAS
  ) => Promise<void>;
  deleteForecast: (isOnboarding?: boolean) => Promise<void>;
  fetchForecast: (queryParams?: QueryParams, forecastIdParam?: string, isOnboarding?: boolean) => Promise<void>;
  fetchForecastReport: (
    page?: Page,
    periodType?: PeriodType,
    forecastIdParam?: string,
    isOnboarding?: boolean
  ) => Promise<void>;
  updateAssumption: (
    data: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>,
    workspaceId: string,
    consolidationId: string,
    forecastId: string,
    assumptionId: string
  ) => Promise<void>;
  deleteAssumption: (
    workspaceId: string,
    consolidationId: string,
    forecastId: string,
    assumptionId: string
  ) => Promise<void>;
  bulkUpdateAssumption: (
    data: Partial<BulkUpdatedPredictionsPayload>,
    workspaceId: string,
    consolidationId: string,
    forecastId: string
  ) => Promise<void>;
  setPeriodType: (periodType: PeriodType) => void;
  syncForecast: (forecast: Forecast) => void;
  forecast: Forecast | ConsolidatedForecastDetail | null;
  forecastReportV2: ConsolidatedForecastReport | null;
  consolidatedForecastReport: ConsolidatedForecastReport | null;
  isFetchingReport?: boolean;
  isLoading?: boolean;
  totalPastDates?: number;
  forecastGraphDate: string;
  periodType: PeriodType;
  rowHeaderWidth: number;
  setRowHeaderWidth: (width: number) => void;
  viewType?: string;
  showPredictionsExplanation?: boolean;
  togglePredictionsExplanation: () => void;
  setViewType?: (viewType: string) => void;
  category?: string;
  setCategory?: (category: string) => void;
  isTogglingPrediction: boolean;
  setIsTogglingPrediction: (isToggling: boolean) => void;
  setPredictionsExplanations: () => void;
  turnOffPredictionsExplanations: () => void;
  isConsolidation: boolean;
  isReadOnly: boolean;
  thisNumberExplained: Awaited<ReturnType<typeof fetchForecastThisNumberExplained>>['rows'];
  totalCashPredictRow: TotalRow | null;
}

interface PredictInfoContextProps {
  children?: ReactNode;
}

export const context = createContext<Partial<PredictionsInfoContextValues>>({});

export const PredictionsInfoContext = ({ children }: PredictInfoContextProps) => {
  const flags = useFlags();
  const { financialConfig } = useContext(PredictManagementContext);
  const [showPredictionsExplanation, setShowPredictionsExplanation] = useState(false);
  const [rowHeaderWidth, setRowHeaderWidth] = useState(INITIAL_ROW_HEADER_WIDTH);
  const [isTogglingPrediction, setIsTogglingPrediction] = useState(false);
  const [category, setCategory] = useState('');
  const [viewType, setViewType] = useState('');

  const location = useLocation();

  const isConsolidation =
    !!matchConsolidationsRoutePath(location.pathname) ||
    !!matchReportingInternalRenderReportUrl(location.pathname, true);

  const organisationContextContent = useDetailContext();
  const consolidationContextContent = useConsolidatedForecastContext();

  const forecastToUse = isConsolidation ? consolidationContextContent.forecast : organisationContextContent.forecast;
  const effectivePermission = forecastToUse?.permission_info?.effective_permission;
  const isReadOnly =
    Boolean(flags.resourceSharing) &&
    Boolean(effectivePermission) &&
    effectivePermission !== 'edit' &&
    (isConsolidation || financialConfig?.reporting_lineref_version === 2);

  useEffect(() => {
    const onRouteChanged = (
      currentLocation: { pathname: string },
      forecast?: Forecast | ConsolidatedForecastListItem | null
    ) => {
      const urlBits = currentLocation.pathname.split('/');
      const page = `${urlBits[urlBits.length - 2]}-${urlBits[urlBits.length - 1]}`;

      if (forecast?.kind === 'budget') {
        setViewType(VIEW_TYPES.ACCRUALS_PL);
        return;
      }

      const shouldSetViewType = !viewType;

      switch (page) {
        case PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_ASSETS:
          if (viewType === VIEW_TYPES.ACCRUALS_PL) {
            setViewType(VIEW_TYPES.ACCRUALS_BS);
          } else if (shouldSetViewType) {
            setViewType(VIEW_TYPES.ACCRUALS_BS);
          }
          setCategory(ASSETS_CATEGORIES.CURRENT_ASSETS);
          break;
        case PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_LIABILITIES:
          if (viewType === VIEW_TYPES.ACCRUALS_PL) {
            setViewType(VIEW_TYPES.ACCRUALS_BS);
          } else if (shouldSetViewType) {
            setViewType(VIEW_TYPES.ACCRUALS_BS);
          }
          setCategory(LIABILITIES_CATEGORIES.CURRENT_LIABILITIES);
          break;
        case PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_OTHER_P_AND_L:
          if (viewType === VIEW_TYPES.ACCRUALS_BS) {
            setViewType(VIEW_TYPES.ACCRUALS_PL);
          } else if (shouldSetViewType) {
            setViewType(VIEW_TYPES.ACCRUALS_PL);
          }
          setCategory(OTHER_PL_CATEGORIES.OTHER_INCOME);
          break;
        default:
          if (viewType === VIEW_TYPES.ACCRUALS_BS) {
            setViewType(VIEW_TYPES.ACCRUALS_PL);
          } else if (shouldSetViewType) {
            setViewType(VIEW_TYPES.ACCRUALS_PL);
          }
      }
    };

    onRouteChanged(location, forecastToUse);

    if (
      (isConsolidation && !consolidationContextContent?.forecast) ||
      (!isConsolidation && !organisationContextContent?.forecast)
    ) {
      setViewType('');
      setCategory('');
    }
  }, [
    consolidationContextContent?.forecast,
    isConsolidation,
    location,
    viewType,
    organisationContextContent?.forecast
  ]);

  const togglePredictionsExplanation = useCallback(() => setShowPredictionsExplanation(!showPredictionsExplanation), [
    showPredictionsExplanation
  ]);

  const setPredictionsExplanations = useCallback(() => setShowPredictionsExplanation(true), []);

  const turnOffPredictionsExplanations = useCallback(() => setShowPredictionsExplanation(false), []);

  const updateRowHeaderWidth = useCallback((newWidth: number) => setRowHeaderWidth(newWidth), []);

  const handleSetIsTogglingPrediction = useCallback((isToggling: boolean) => setIsTogglingPrediction(isToggling), []);

  const contextData = useMemo(() => {
    const sharedData = {
      category,
      setCategory,
      viewType,
      setViewType,
      showPredictionsExplanation,
      rowHeaderWidth,
      isTogglingPrediction,
      setIsTogglingPrediction: handleSetIsTogglingPrediction,
      togglePredictionsExplanation,
      turnOffPredictionsExplanations,
      setPredictionsExplanations,
      setRowHeaderWidth: updateRowHeaderWidth,
      isConsolidation,
      isReadOnly
    };
    if (isConsolidation) {
      return { ...consolidationContextContent, ...sharedData };
    } else {
      return { ...organisationContextContent, ...sharedData };
    }
  }, [
    category,
    viewType,
    showPredictionsExplanation,
    rowHeaderWidth,
    isTogglingPrediction,
    handleSetIsTogglingPrediction,
    togglePredictionsExplanation,
    turnOffPredictionsExplanations,
    setPredictionsExplanations,
    updateRowHeaderWidth,
    isConsolidation,
    isReadOnly,
    consolidationContextContent,
    organisationContextContent
  ]);

  return (
    <context.Provider value={{ ...contextData }}>
      {!isConsolidation && <SyncCompleteHandler fetchForecastReport={organisationContextContent.fetchForecastReport} />}
      {children}
    </context.Provider>
  );
};

export const usePredictionsInfoContext = () => useContext(context);
